/**
 * The geometry used by LVGL 7's lv_arc::get_center().
 *
 * LVGL does not centre an arc in the widget rectangle after applying a
 * symmetric inset. It first subtracts the four LV_ARC_PART_BG paddings from
 * the two axes, takes the integer half of the smaller result, and anchors the
 * centre at the top-left padded corner plus that radius.
 */

function lvglCoord(value) {
    const number = Number(value)
    return Number.isFinite(number) ? Math.trunc(number) : 0
}

/** C integer division truncates towards zero (unlike Math.floor for negatives). */
export function lvglDiv2(value) {
    const integer = lvglCoord(value)
    return integer < 0 ? Math.ceil(integer / 2) : Math.floor(integer / 2)
}

function lvglDiv(value, divisor) {
    const number = lvglCoord(value)
    return number < 0 ? Math.ceil(number / divisor) : Math.floor(number / divisor)
}

export function arcKnobCount({ type = 0, adjustable = false } = {}) {
    if (!adjustable) return 0
    return Number(type) === 3 ? 2 : 1
}

/** LVGL 7's integer angle approximation used by lv_arc's pointer handler. */
export function lvglAtan2(x, y) {
    let negflag = 0
    let ux = x
    let uy = y
    if (ux < 0) { negflag += 0x01; ux = 0 - ux }
    if (uy < 0) { negflag += 0x02; uy = 0 - uy }

    let degree
    if (ux > uy) {
        degree = lvglDiv(uy * 45, ux)
        negflag += 0x10
    } else {
        degree = lvglDiv(ux * 45, uy)
    }

    let comp = 0
    const tempdegree = degree
    if (tempdegree > 22) {
        if (tempdegree <= 44) comp++
        if (tempdegree <= 41) comp++
        if (tempdegree <= 37) comp++
        if (tempdegree <= 32) comp++
    } else {
        if (tempdegree >= 2) comp++
        if (tempdegree >= 6) comp++
        if (tempdegree >= 10) comp++
        if (tempdegree >= 15) comp++
    }
    degree += comp
    if (negflag & 0x10) degree = 90 - degree

    if (negflag & 0x02) {
        degree = (negflag & 0x01) ? 180 + degree : 180 - degree
    } else if (negflag & 0x01) {
        degree = 360 - degree
    }
    return degree
}

// LVGL 7.11.1's _lv_trigo_sin() uses this 1-degree lookup table, with
// LV_TRIGO_SIN_MAX=32767 and LV_TRIGO_SHIFT=15.
const LVGL_SIN_0_90 = [
    0, 572, 1144, 1715, 2286, 2856, 3425, 3993, 4560, 5126, 5690, 6252, 6813, 7371, 7927, 8481,
    9032, 9580, 10126, 10668, 11207, 11743, 12275, 12803, 13328, 13848, 14364, 14876, 15383, 15886, 16383, 16876,
    17364, 17846, 18323, 18794, 19260, 19720, 20173, 20621, 21062, 21497, 21925, 22347, 22762, 23170, 23571, 23964,
    24351, 24730, 25101, 25465, 25821, 26169, 26509, 26841, 27165, 27481, 27788, 28087, 28377, 28659, 28932, 29196,
    29451, 29697, 29934, 30162, 30381, 30591, 30791, 30982, 31163, 31335, 31498, 31650, 31794, 31927, 32051, 32165,
    32269, 32364, 32448, 32523, 32587, 32642, 32687, 32722, 32747, 32762, 32767,
]

export function lvglTrigoSin(angle) {
    let normalized = lvglCoord(angle) % 360
    if (normalized < 0) normalized += 360
    let value
    if (normalized < 90) value = LVGL_SIN_0_90[normalized]
    else if (normalized < 180) value = LVGL_SIN_0_90[180 - normalized]
    else if (normalized < 270) value = -LVGL_SIN_0_90[normalized - 180]
    else value = -LVGL_SIN_0_90[360 - normalized]
    return value === 0 ? 0 : value
}

/** LVGL 7's integer _lv_map() used by lv_arc::value_update(). */
export function lvglMap(value, minIn, maxIn, minOut, maxOut) {
    const x = lvglCoord(value)
    const lower = lvglCoord(minIn)
    const upper = lvglCoord(maxIn)
    const outMin = lvglCoord(minOut)
    const outMax = lvglCoord(maxOut)
    if (x >= upper) return outMax
    if (x <= lower) return outMin
    return lvglDiv((x - lower) * (outMax - outMin), upper - lower) + outMin
}

/** Resolve the indicator angles and the LVGL knob angle for one value. */
export function lvglArcValueAngles({ type = 0, min = 0, max = 100, value = 0, startValue = min, startAngle = 0, endAngle = 0 } = {}) {
    const start = lvglCoord(startAngle)
    const configuredEnd = lvglCoord(endAngle)
    const end = configuredEnd < start ? configuredEnd + 360 : configuredEnd
    const midpoint = lvglDiv2(start + end)
    const rangeMidpoint = lvglDiv2(lvglCoord(min) + lvglCoord(max))
    const current = lvglCoord(value)

    if (type === 1) {
        if (current < rangeMidpoint) {
            const angle = lvglMap(current, min, rangeMidpoint, start, midpoint)
            return {
                start: angle,
                end: midpoint,
                knobAngle: angle,
                rangeMidpoint,
            }
        }
        const angle = lvglMap(current, rangeMidpoint, max, midpoint, end)
        return { start: midpoint, end: angle, knobAngle: angle, rangeMidpoint }
    }

    if (type === 3) {
        const rangeStart = lvglMap(startValue, min, max, start, end)
        const rangeEnd = lvglMap(current, min, max, start, end)
        return {
            start: rangeStart,
            end: rangeEnd,
            knobAngle: rangeEnd,
            startKnobAngle: rangeStart,
            endKnobAngle: rangeEnd,
            rangeMidpoint,
        }
    }

    const angle = lvglMap(current, min, max, start, end)
    if (type === 2) return { start: angle, end: configuredEnd, knobAngle: angle, rangeMidpoint }
    return { start, end: angle, knobAngle: angle, rangeMidpoint }
}

/** Convert a pointer position using the same integer angle/value path as lv_arc. */
export function lvglArcValueFromPoint({ center, point, min = 0, max = 100, startAngle = 0, endAngle = 0, rotation = 0 } = {}) {
    const x = lvglCoord(point?.x) - lvglCoord(center?.x)
    const y = lvglCoord(point?.y) - lvglCoord(center?.y)
    if (x === 0 && y === 0) return lvglCoord(min)

    const start = lvglCoord(startAngle)
    let end = lvglCoord(endAngle)
    if (end < start) end += 360

    let relative = lvglAtan2(y, x) - lvglCoord(rotation) - start
    if (relative < 0) relative += 360
    const degreeRange = end - start
    relative = Math.max(0, Math.min(degreeRange, relative))
    return lvglMap(start + relative, start, end, min, max)
}

/** The integer point at which LVGL anchors LV_ARC_PART_KNOB. */
export function lvglArcKnobAnchor({ center, radius, indicatorLineWidth, angle } = {}) {
    const halfLineWidth = lvglDiv2(indicatorLineWidth)
    const pointRadius = lvglCoord(radius) - halfLineWidth
    const knobX = (pointRadius * lvglTrigoSin(lvglCoord(angle) + 90)) >> 15
    const knobY = (pointRadius * lvglTrigoSin(lvglCoord(angle))) >> 15
    return {
        x: lvglCoord(center?.x) + knobX,
        y: lvglCoord(center?.y) + knobY,
        pointRadius,
        halfLineWidth,
    }
}

/** LVGL's inclusive lv_area_t converted to Konva's x/y/width/height form. */
export function lvglArcKnobBounds({ anchor, indicatorLineWidth, pad_top = 0, pad_right = 0, pad_bottom = 0, pad_left = 0 } = {}) {
    const lineWidth = lvglCoord(indicatorLineWidth)
    const halfLineWidth = lvglDiv2(lineWidth)
    const top = lvglCoord(pad_top)
    const right = lvglCoord(pad_right)
    const bottom = lvglCoord(pad_bottom)
    const left = lvglCoord(pad_left)
    // lv_area_t is inclusive: x1 = anchor - pad - half and
    // x2 = anchor + pad + half, so an even line width still occupies
    // 2 * trunc(width / 2) + 1 pixels.
    const baseSize = (2 * halfLineWidth) + 1
    return {
        x: lvglCoord(anchor?.x) - left - halfLineWidth,
        y: lvglCoord(anchor?.y) - top - halfLineWidth,
        width: baseSize + left + right,
        height: baseSize + top + bottom,
        anchor: { x: lvglCoord(anchor?.x), y: lvglCoord(anchor?.y) },
    }
}

export function lvglArcGeometry({
    width,
    height,
    pad_top = 0,
    pad_right = 0,
    pad_bottom = 0,
    pad_left = 0,
    indicatorPads = {},
} = {}) {
    const w = lvglCoord(width)
    const h = lvglCoord(height)
    const top = lvglCoord(pad_top)
    const right = lvglCoord(pad_right)
    const bottom = lvglCoord(pad_bottom)
    const left = lvglCoord(pad_left)

    const availableWidth = w - left - right
    const availableHeight = h - top - bottom
    const radius = lvglDiv2(Math.min(availableWidth, availableHeight))

    const indicTop = lvglCoord(indicatorPads.pad_top)
    const indicRight = lvglCoord(indicatorPads.pad_right)
    const indicBottom = lvglCoord(indicatorPads.pad_bottom)
    const indicLeft = lvglCoord(indicatorPads.pad_left)
    const indicatorInset = Math.max(indicTop, indicRight, indicBottom, indicLeft)

    return {
        // This is the effective padded box. lv_arc does not pass this box as
        // its draw clip; the renderer still receives the outer object clip.
        content: {
            x: left,
            y: top,
            width: availableWidth,
            height: availableHeight,
        },
        center: {
            x: left + radius,
            y: top + radius,
        },
        radius,
        indicatorRadius: radius - indicatorInset,
    }
}
